import { useEffect, useRef, useState, type RefObject } from 'react'
import launchpadTag from 'url:asset/launchpad/launchpad-tag.svg'
import TiltedPhone from './TiltedPhone'
import tapVideo from 'url:asset/launchpad/launchpad-tag-tap.mp4'
import tapFirstFrame from 'url:asset/launchpad/launchpad-tag-tap-first.jpg'
import tapLastFrame from 'url:asset/launchpad/launchpad-tag-tap-last.jpg'

// Launchpad Tags uses a lighter pink than the site accent (Figma node 3161:633)
const PINK = '#ffa8a8'

// On scroll the phone tips past its resting pose into the tag (the tap), then
// rotates back. The angle is relative to rest, pivoting near the bottom of the phone.
const PHONE_TAP = 'rotate(8deg)'
const TAP_MS = 1100
const TAP_AT = 0.45 // fraction of the animation where the phone touches the tag
const TRIGGER_VISIBLE = 0.9 // fraction of the artwork on screen before the tap plays

// Hidden until there's somewhere for "Explore Launchpad Tags" to link to
const SHOW_EXPLORE_BUTTON = false

type TapPhase = 'waiting' | 'approaching' | 'tapped'

export default function LaunchpadTagsSection() {
  const artworkRef = useRef<HTMLDivElement>(null)
  const phoneRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const { phase, replay } = useTapAnimation(artworkRef, phoneRef)

  // The screen shows still frames, and the recording only while it plays: the first
  // frame before the tap, then the video (its NFC banner appears in its first frames),
  // then the last frame once it ends
  const [screen, setScreen] = useState<'first' | 'video' | 'last'>('first')
  const firstFrameRef = useRef<HTMLImageElement>(null)
  const lastFrameRef = useRef<HTMLImageElement>(null)

  // Decode the stills up front; hidden images are otherwise decoded when first shown
  useEffect(() => {
    firstFrameRef.current?.decode().catch(() => {})
    lastFrameRef.current?.decode().catch(() => {})
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    if (phase === 'tapped') {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        setScreen('last')
        return
      }

      // Only uncover the video once it has drawn a frame from the restart, so a
      // stale frame from the previous play can never show
      let cancelled = false
      const reveal = () => { if (!cancelled) setScreen('video') }
      if ('requestVideoFrameCallback' in video) {
        video.requestVideoFrameCallback(reveal)
      } else {
        (video as HTMLVideoElement).addEventListener('timeupdate', reveal, { once: true })
      }

      video.currentTime = 0
      video.play().catch(() => setScreen('last'))
      return () => { cancelled = true }
    } else if (phase === 'waiting') {
      video.pause()
      setScreen('first')
    }
  }, [phase])

  return <div className="py-16 px-8 overflow-x-clip">
    <div className="max-w-[654px] mx-auto flex flex-col items-center gap-6">
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-2xl leading-none">Launchpad Tags</p>
          <span
            className="h-[25px] w-[72px] flex items-center justify-center rounded-full border border-[rgba(233,145,145,0.5)] text-white font-semibold text-[17px] leading-none"
            style={{ background: PINK }}
          >
            BETA
          </span>
        </div>

        <h1 className="font-semibold text-4xl md:text-[42px] flex items-center gap-3" style={{ color: PINK }}>
          <TapIcon className="h-[0.95em] w-auto" />
          Tap to Launch
        </h1>
      </div>

      {/* Artwork is laid out at Figma's 593×509 size, then scaled down on small screens */}
      <div ref={artworkRef} className="relative w-[368px] h-[316px] sm:w-[534px] sm:h-[458px] md:w-[593px] md:h-[509px]">
        <div className="absolute top-0 left-0 w-[593px] h-[509px] origin-top-left scale-[0.62] sm:scale-90 md:scale-100">
          <div className="absolute left-[180px] top-[10px] w-[418px] h-[333px] flex items-center justify-center">
            <div className="-skew-x-13 scale-y-97">
              <div className="relative size-[290px]">
                <div className="absolute inset-[-5.56%_-6.74%_-7.91%_-6.74%]">
                  <img src={launchpadTag} className="block max-w-none size-full" draggable={false} />
                </div>
              </div>
            </div>
          </div>

          <div className="absolute left-[18px] top-[23px] w-[377px] h-[464px] flex items-center justify-center">
            <div ref={phoneRef} style={{ transformOrigin: '30% 85%' }}>
              <TiltedPhone width={280} className="rotate-20">
                {/* The video sits at the bottom and is never hidden; the stills cover it
                    before and after playback. Uncovering only ever reveals a video frame that
                    matches the still, so a slow repaint can't flash the wrong frame. */}
                <video
                  ref={videoRef}
                  src={tapVideo}
                  muted
                  playsInline
                  preload="auto"
                  onEnded={() => setScreen('last')}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <img
                  ref={lastFrameRef}
                  src={tapLastFrame}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ visibility: screen === 'last' ? 'visible' : 'hidden' }}
                  draggable={false}
                />
                <img
                  ref={firstFrameRef}
                  src={tapFirstFrame}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ visibility: screen === 'first' ? 'visible' : 'hidden' }}
                  draggable={false}
                />
              </TiltedPhone>
            </div>
          </div>
        </div>
      </div>

      {/* Fades in once the recording has finished; keeps its space so nothing shifts */}
      <button
        onClick={replay}
        disabled={screen !== 'last'}
        className="px-4 py-1.5 text-sm font-semibold cursor-pointer transition-opacity duration-300 disabled:opacity-0 disabled:pointer-events-none"
        style={{ color: PINK }}
      >
        Replay
      </button>

      <div className="w-full font-semibold text-xl md:text-2xl leading-[1.2] text-[#888888]">
        <p>
          <span className="text-black">All they have to do is tap.</span>{" "}
          Adhere Launchpad Tag to a chair, table, or other surface, and your church goes right where you need them to.
        </p>

        <p className="mt-[1.2em]">
          And <span className="text-black">Scheduled Changes</span> enable Launchpad Tag to open different Launchpad Views based on the date and time. So Youth gets their services, and Sunday can see your main services.
        </p>
      </div>

      {SHOW_EXPLORE_BUTTON && (
        <a
          href="#"
          className="h-[55px] w-[339px] max-w-full flex items-center justify-between pl-[23px] pr-[5px] rounded-full text-white font-bold text-[19px] sm:text-[22.336px] whitespace-nowrap hover:opacity-75 transition-opacity duration-300"
          style={{ background: PINK }}
        >
          Explore Launchpad Tags
          <span className="flex items-center justify-center size-[45px] rounded-full bg-white" style={{ color: PINK }}>
            <svg viewBox="0 0 24 24" className="size-6" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </span>
        </a>
      )}
    </div>
  </div>
}

// Plays the tap once the artwork is nearly all on screen, and reports the phase.
// `replay` restarts it from the resting pose.
function useTapAnimation(
  triggerRef: RefObject<HTMLElement | null>,
  phoneRef: RefObject<HTMLElement | null>,
): { phase: TapPhase, replay: () => void } {
  const [phase, setPhase] = useState<TapPhase>('waiting')
  const tapTimer = useRef<number | undefined>(undefined)

  const play = () => {
    const phone = phoneRef.current
    if (!phone) return

    window.clearTimeout(tapTimer.current)
    phone.getAnimations().forEach(a => a.cancel())

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setPhase('tapped')
      return
    }

    setPhase('approaching')
    phone.animate(
      [
        { transform: 'none', easing: 'cubic-bezier(0.4, 0, 0.2, 1)' },
        { transform: PHONE_TAP, offset: TAP_AT, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' },
        { transform: 'none' },
      ],
      { duration: TAP_MS },
    )
    tapTimer.current = window.setTimeout(() => setPhase('tapped'), TAP_MS * TAP_AT)
  }

  useEffect(() => {
    const trigger = triggerRef.current
    if (!trigger) return

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return
      observer.disconnect()
      play()
    }, { threshold: TRIGGER_VISIBLE })

    observer.observe(trigger)
    return () => {
      observer.disconnect()
      window.clearTimeout(tapTimer.current)
    }
  }, [])

  const replay = () => {
    window.clearTimeout(tapTimer.current)
    phoneRef.current?.getAnimations().forEach(a => a.cancel())
    setPhase('waiting')
    // Pause briefly so the screen can go dark before the phone taps again
    tapTimer.current = window.setTimeout(play, 500)
  }

  return { phase, replay }
}

function TapIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 40" className={className} fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round">
      <path d="M3 14a8 8 0 0 1 0 12" />
      <path d="M9 9a15 15 0 0 1 0 22" />
      <path d="M15 4a22 22 0 0 1 0 32" />
    </svg>
  )
}
